import React, {Component} from 'react'
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  Grid,
  GridItem,
  Card,
  CardBody,
  Badge,
  Divider,
  HStack,
  Icon
} from '@chakra-ui/react';
import { ArrowBackIcon, CalendarIcon, AtSignIcon } from '@chakra-ui/icons';
import './BerichtInfo.css'
import Sidebar from '../Sidebar/Sidebar';
import Navigation from '../../Navigation/Navigation';
import MobileMenu from '../MobileMenu/MobileMenu';
import Footer from '../../../components/Footer/Footer';
import colors from '../../../theme/colors';

class BerichtInfo extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isMobileMenuOpen: false
    };
  }

  componentDidMount() {
    // Mark message as read if user is recipient and message is unread
    const message = this.props.state.selectedMessage;
    if (message &&
        message.recipient_username === this.props.state.username &&
        !message.is_read &&
        this.props.onMarkMessageAsRead) {
      this.props.onMarkMessageAsRead(message.id);
    }
  }

  handleOpenMobileMenu = () => {
    this.setState({ isMobileMenuOpen: true })
  }

  handleCloseMobileMenu = () => {
    this.setState({ isMobileMenuOpen: false })
  }

  componentWillUnmount() {
    // Ensure mobile menu is closed when component unmounts
    if (this.state.isMobileMenuOpen) {
      this.setState({ isMobileMenuOpen: false })
    }
  }
  render () {
    const message = this.props.state.selectedMessage;
    
    if (!message) {
      return (
        <Box p="40px" textAlign="center">
          <Text color="gray.500">Geen bericht geselecteerd</Text>
          <Button 
            mt={4} 
            onClick={() => this.props.onRouteChange('berichten')}
            colorScheme="blue"
            variant="outline"
          >
            Terug naar berichten
          </Button>
        </Box>
      );
    }

    const isReceived = message.recipient_username === this.props.state.username;
    const otherUser = isReceived ? message.sender_username : message.recipient_username;

    return (
      <>
        <Grid
        templateColumns="repeat(24, 1fr)"
        gap={0}
        minH="100vh"
        bg="gray.50"
        >
        <GridItem
        as="aside"
        colSpan={{ base: 0, md: 4, lg: 3 }} 
          display={{ base: "none", md: "block" }}
        bg="white"
        borderRight="1px solid"
        borderColor="gray.200"
        >
        <Sidebar
        state = {this.props.state}
        userVersion = {this.props.state.userVersion}
        onRouteChange = {this.props.onRouteChange}
        onLogout = {this.props.onLogout}
        />

        </GridItem>
        <GridItem
        as="main"
        colSpan={{ base: 24, md: 20, lg: 21 }}
        p="10px"
        display="flex"
        flexDirection="column"
        minHeight="100vh"
        >
        <Navigation
        state = {this.props.state}
        onRouteChange = {this.props.onRouteChange}
        getFeed = {this.props.getFeed}
        onVersionChange = {this.props.onVersionChange}
        onLogout = {this.props.onLogout}
        onOpenFeedback = {this.props.onOpenFeedback}
        onOpenMobileMenu = {this.handleOpenMobileMenu}
        />

        <Box p="40px" flex="1">
          <VStack spacing={6} align="stretch">
            {/* Header with back button */}
            <HStack spacing={4}>
              <Button
                leftIcon={<ArrowBackIcon />}
                variant="ghost"
                onClick={() => this.props.onRouteChange('berichten')}
                size="sm"
              >
                Terug naar berichten
              </Button>
            </HStack>

            <Box maxW="800px" mx="auto" w="100%">
              <VStack spacing={6} align="stretch">
                <Card shadow="lg" bg="white">
                  <CardBody p={8}>
                    <VStack align="start" spacing={6}>
                      
                      {/* Message Header */}
                      <VStack align="start" spacing={3} w="100%">
                        <HStack justify="space-between" w="100%">
                          <Heading as="h2" size="lg" color="gray.700">
                            {message.subject}
                          </Heading>
                          {isReceived && (
                            <Badge 
                              colorScheme={message.is_read ? 'gray' : 'blue'}
                              size="lg"
                              variant="solid"
                            >
                              {message.is_read ? 'Gelezen' : 'Nieuw'}
                            </Badge>
                          )}
                        </HStack>
                        
                        <HStack spacing={6} color="gray.500" fontSize="sm">
                          <HStack spacing={2}>
                            <Icon as={AtSignIcon} />
                            <Text>
                              {isReceived ? 'Van' : 'Naar'}: <strong>{otherUser}</strong>
                            </Text>
                          </HStack>
                          <HStack spacing={2}>
                            <Icon as={CalendarIcon} />
                            <Text>
                              {message.created_at ? 
                                new Date(message.created_at).toLocaleDateString('nl-NL', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : 
                                'Onbekende datum'
                              }
                            </Text>
                          </HStack>
                        </HStack>
                      </VStack>
                      
                      {/* Suggestion Context - if message is related to a suggestion */}
                      {message.suggestion_id && (
                        <>
                          <Divider />
                          <Box bg="blue.50" p={4} borderRadius="md" border="1px solid" borderColor="blue.200">
                            <VStack align="start" spacing={2}>
                              <HStack spacing={2}>
                                <Badge colorScheme="blue" variant="solid">
                                  Gerelateerde Suggestie
                                </Badge>
                                <Text fontSize="sm" color="blue.600" fontWeight="semibold">
                                  ID: {message.suggestion_id}
                                </Text>
                              </HStack>
                              <Text fontSize="sm" color="blue.700">
                                {message.suggestion_text}
                              </Text>
                            </VStack>
                          </Box>
                        </>
                      )}
                      
                      <Divider />
                      
                      {/* Message Content */}
                      <Box>
                        <Text fontSize="sm" color="gray.500" fontWeight="semibold" mb={3}>
                          BERICHT
                        </Text>
                        <Text 
                          color="gray.700" 
                          lineHeight="1.6"
                          whiteSpace="pre-wrap"
                          fontSize="md"
                        >
                          {message.text}
                        </Text>
                      </Box>
                      
                      <Divider />
                      
                      {/* Actions */}
                      <VStack align="start" spacing={4} width="100%">
                        <Text fontSize="sm" color="gray.500" fontWeight="semibold">
                          ACTIES
                        </Text>
                        <VStack spacing={3} align="stretch" width="100%">
                          {isReceived && (
                            <Button
                              bg={colors.primary.main}
                              color="white"
                              _hover={{ bg: colors.primary.dark }}
                              onClick={() => this.props.onReplyToMessage && this.props.onReplyToMessage(message)}
                              size={{ base: "sm", md: "md" }}
                              width={{ base: "100%", md: "auto" }}
                            >
                              Beantwoorden
                            </Button>
                          )}
                          {!message.is_read && isReceived && this.props.onMarkMessageAsRead && (
                            <Button
                              colorScheme="gray"
                              onClick={() => this.props.onMarkMessageAsRead(message.id)}
                              size={{ base: "sm", md: "md" }}
                              width={{ base: "100%", md: "auto" }}
                            >
                              Markeer als gelezen
                            </Button>
                          )}
                          {message.read_at && isReceived && (
                            <Text fontSize={{ base: "sm", md: "md" }} color="green.500">
                              ✓ Gelezen op {new Date(message.read_at).toLocaleDateString('nl-NL')}
                            </Text>
                          )}
                        </VStack>
                      </VStack>
                    </VStack>
                  </CardBody>
                </Card>
              </VStack>
            </Box>
          </VStack>
        </Box>
        <Footer onRouteChange={this.props.onRouteChange} />

        </GridItem>
        </Grid>
      
        {/* Mobile Menu Drawer */}
        <MobileMenu
          isOpen={this.state.isMobileMenuOpen}
          onClose={this.handleCloseMobileMenu}
          onRouteChange={this.props.onRouteChange}
          userVersion={this.props.state.userVersion}
          resetSuggestionsPage={this.props.resetSuggestionsPage}
          onLogout={this.props.onLogout}
        />
      </>
    )
  }
}

export default BerichtInfo
